import { RootState, store } from "@/store/index";
import AllSocialPublish, {
  type PresetPlatformControl,
} from "@/components/MediaPublish/SocialPublish/SocialPublish";
import { useEffect, useRef, useState } from "react";
import { ConnectedProps, connect } from "react-redux";
import { getAssets, getListMedia } from "@/store/media/media.action";
import { updateModal } from "@/store/app/app.slice";
import {
  emptyAssetDetails,
  emptyMediaDetails,
  clearBorrowedMediaSocials,
  setIsEditPageChanged,
} from "@/store/media/media.slice";
import { Button } from "../../../components";
import { Alert } from "@/utils/ui/Alert/Alert";
import { getPresetList } from "@/store/media/media.action";
import Modal from "@/components/Modal/Modal";
import SelectPreset from "@/components/MetaPresets/SelectPreset";
import PresetCardList from "@/components/MetaPresets/PresetCardList";
import { MEDIA_STATUS } from "@/utils/constants/uiConstants";
import { PresetListModel } from "@/database";
import {
  filterAdminPresetsByTeamsAndType,
  filterPresetsByTeamsAndType,
  mediaSocialsToPresetSocialPayload,
} from "@/utils/mediautils/CommonFunctions";
import { getAdminPresetList } from "@/store/media/media.action";
import { convertChannelConfigToSocial } from "@/utils/mediautils/CommonFunctions";

interface IWizardProps {
  isWizard?: boolean;
  closeHandler?: any;
}

const MediaPublish = ({
  isWizard = false,
  closeHandler,
  updateModal,
  emptyAssetDetails,
  getPresetList,
  presetList,
  emptyMediaDetails,
  currentMediaDetailsForEdit,
  singleCurrentUser,
  getAssets,
  bunchProductList,
  getAdminPresetList,
  mediaAssets,
  adminPresetList,
  borrowedMediaSocials,
  clearBorrowedMediaSocials,
}: Props) => {
  const [presetId, setPresetId] = useState<string>("");
  const [socialPreset, setSocialPreset] = useState<PresetListModel[]>([]);
  const [socialPresetDetails, setSocialPresetDetails] =
    useState<PresetListModel>({} as PresetListModel);
  const [isOpenPreviewPreset, setIsOpenPreviewPreset] =
    useState<boolean>(false);
  const [mediaData, setMediaData] = useState<any>([]);
  const [isOpenSelectPreset, setIsOpenSelectPreset] = useState<boolean>(false);
  const [metaPresetData, setMetaPresetData] = useState<any>([]);
  // Per-preset excluded (deselected) destination channel ids - shared source of
  // truth between PresetCardList's toggles and the SocialPublish sheet's tab ×.
  const [excludedByPreset, setExcludedByPreset] = useState<
    Record<string, string[]>
  >({});
  const presetPlatformRef = useRef<PresetPlatformControl | null>(null);

  const socialPresetRef = useRef<any>([]);

  // Mirrors MediaDetails.tsx's handleHasChanges: any card added/edited/removed on
  // this Publish step marks the same redux flag the bulk-upload wizard's tab-bar
  // guard (Create.tsx) already reads to warn before a tab switch/close discards
  // unsaved work - previously only the Details tab fed that flag, so an added-but-
  // unsaved social/playlist card here was silently discardable.
  const handlePublishDirty = () => {
    store.dispatch(setIsEditPageChanged(true));
  };

  const handleNext = () => {
    setIsOpenPreviewPreset(false);
  };

  const handleSocialPresetDetails = (value: any) => {
    setSocialPresetDetails(value);
    socialPresetRef.current = value;
  };

  useEffect(() => {
    if (currentMediaDetailsForEdit?.media?.id && mediaAssets.length === 0) {
      getAssets({
        pid: currentMediaDetailsForEdit?.media?.simulive?.parentMediaId
          ? currentMediaDetailsForEdit?.media?.simulive.parentMediaId
          : currentMediaDetailsForEdit?.media?.id,
      });
    }

    return () => {
      emptyAssetDetails();
    };
  }, [currentMediaDetailsForEdit?.media?.id]);

  useEffect(() => {
    const onDoneClick = () => {
      emptyAssetDetails();
      emptyMediaDetails();
      updateModal(false);
      if (closeHandler) {
        closeHandler();
      }
    };
    window.addEventListener("doneClick", onDoneClick);
    return () => {
      window.removeEventListener("doneClick", onDoneClick);
    };
    // Re-subscribe whenever closeHandler changes (e.g. bulk wizard tab
    // switches) - this component instance is reused across tabs, so a
    // mount-only listener would keep calling the first tab's closeHandler.
  }, [closeHandler, emptyAssetDetails, emptyMediaDetails, updateModal]);

  useEffect(() => {
    getAdminPresetList({ keys: ["publish_preset"], status: "active" });
  }, []);

  useEffect(() => {
    setSocialPreset(
      filterAdminPresetsByTeamsAndType(
        bunchProductList,
        adminPresetList,
        "publish_preset"
      )
    );
  }, [adminPresetList, bunchProductList]);

  // useEffect(() => {
  //   setSocialPreset(
  //     filterPresetsByTeamsAndType(bunchProductList, presetList, "social")
  //   );
  // }, [presetList, bunchProductList]);

  const handleSelectPreset = (preset: any) => {
    setPresetId(preset.id);
    setIsOpenSelectPreset(false);
  };

  const handleSelectSocialPreset = (preset: any) => {
    // A channel already published for this media is no longer excluded here -
    // selecting it via a preset should behave exactly like the chip/dropdown
    // flow (SocialPublish.tsx's handleChannelChange): reopen a still-open
    // local draft, or start a fresh republish attempt when every existing
    // record for that channel is already live. Filtering it out here would
    // silently skip opening the sheet at all for an already-published
    // destination.
    setMediaData(() => [...(preset?.social || [])]);
  };

  useEffect(() => {
    if (presetId) {
      const preset = adminPresetList?.publish_preset?.find(
        (preset: any) => preset.id === presetId
      );

      const convertedPreset = convertChannelConfigToSocial(preset);
      // Respect whatever channels are currently excluded for this preset
      // (via PresetCardList's per-channel toggles) - otherwise this effect,
      // which re-fires on every presetId change, would stomp applyPreset's
      // filtered selection with the full unfiltered preset.
      const excludedPlatforms = excludedByPreset[presetId] || [];
      convertedPreset.social = (convertedPreset.social || []).filter(
        (item: any) => !excludedPlatforms.includes(item.platform || "")
      );
      handleSelectSocialPreset(convertedPreset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetId, presetList]);

  // Pre-open social channels borrowed from another media via
  // "Reuse Meta" -> select media, same way a selected publish preset does.
  useEffect(() => {
    if (
      !borrowedMediaSocials?.sourceMediaId ||
      !currentMediaDetailsForEdit?.media?.id ||
      borrowedMediaSocials.sourceMediaId === currentMediaDetailsForEdit.media.id
    ) {
      return;
    }

    handleSelectSocialPreset({
      social: mediaSocialsToPresetSocialPayload(borrowedMediaSocials.socials),
    });
    clearBorrowedMediaSocials();
  }, [borrowedMediaSocials, currentMediaDetailsForEdit?.media?.id]);

  useEffect(() => {
    if (
      bunchProductList.length > 0 &&
      adminPresetList?.publish_preset &&
      adminPresetList?.publish_preset?.length > 0
    ) {
      const presetId = localStorage.getItem("socialPresetId");

      const presetData = adminPresetList?.publish_preset?.find(
        (item: any) => item?.id === presetId
      );

      const convertedPreset = convertChannelConfigToSocial(presetData || {});

      setMetaPresetData(convertedPreset);
    } else {
      setMetaPresetData([]);
    }
  }, [bunchProductList, adminPresetList?.publish_preset]);

  const convertedSocialPresets = (socialPreset || []).map((preset: any) => ({
    ...preset,
    ...convertChannelConfigToSocial(preset),
  }));

  // Turns a preset switch fully off: pulls every one of its destinations out
  // of the sheet and clears its selection/exclusion bookkeeping.
  const removePresetEntirely = (preset: PresetListModel) => {
    (preset?.social || []).forEach((item: any) => {
      if (item.platform) {
        presetPlatformRef.current?.removeChannel(item.platform);
      }
    });
    setPresetId("");
    setMediaData([]);
    setExcludedByPreset((prev) => {
      if (!(preset.id in prev)) return prev;
      const next = { ...prev };
      delete next[preset.id];
      return next;
    });
  };

  // Applies a preset (or re-applies it with an updated exclusion list) -
  // always treated as "turn on" / "update selection", never as an off-toggle.
  const applyPreset = (
    preset: PresetListModel,
    excludedPlatforms: string[]
  ) => {
    setPresetId(preset?.id || "");

    const convertedPreset = { ...preset };
    convertedPreset.social = (convertedPreset.social || []).filter(
      (item: any) => !excludedPlatforms.includes(item.platform || "")
    );

    handleSelectSocialPreset(convertedPreset);
  };

  const handlePresetCardApply = (
    preset: PresetListModel,
    excludedPlatforms: string[]
  ) => {
    // Toggling the already-selected preset's switch off: pull every one of its
    // destinations out of the sheet and clear its selection, instead of
    // silently re-applying the same preset (the switch had no "off" path).
    if (presetId === preset?.id) {
      removePresetEntirely(preset);
      return;
    }

    applyPreset(preset, excludedPlatforms);
  };

  // List-side platform toggle: keep the shared exclusion bookkeeping in sync.
  // Turning a channel ON when its preset isn't applied yet applies the preset
  // with just that one channel selected; turning off the last remaining
  // channel turns the whole preset off (closes the sheet, resets the switch).
  const handleTogglePresetPlatform = (
    targetPresetId: string,
    platform: string
  ) => {
    const preset = convertedSocialPresets.find(
      (p: any) => p.id === targetPresetId
    );
    if (!preset) return;
    const allPlatforms: string[] = (preset.social || []).map(
      (item: any) => item.platform || ""
    );
    const excluded = excludedByPreset[targetPresetId] || [];

    if (presetId !== targetPresetId) {
      // Preset isn't applied yet, so every channel renders unchecked - a
      // click here always means "turn this one on", regardless of what's in
      // excludedByPreset (that bookkeeping only matters once the preset is
      // actually applied).
      const next = allPlatforms.filter((p) => p !== platform);
      setExcludedByPreset((prev) => ({ ...prev, [targetPresetId]: next }));
      applyPreset(preset, next);
      return;
    }

    const turningOn = excluded.includes(platform);
    const next = turningOn
      ? excluded.filter((p) => p !== platform)
      : [...excluded, platform];

    if (
      !turningOn &&
      allPlatforms.length > 0 &&
      allPlatforms.every((p) => next.includes(p))
    ) {
      // That was the last remaining channel - turn the preset off entirely.
      removePresetEntirely(preset);
      return;
    }

    setExcludedByPreset((prev) => ({ ...prev, [targetPresetId]: next }));
    if (!turningOn && presetPlatformRef.current) {
      presetPlatformRef.current.removeChannel(platform);
    } else if (turningOn) {
      // Re-apply the already-selected preset with the updated exclusion list
      // so the re-enabled channel actually reopens - applyPreset (not
      // handlePresetCardApply) so this isn't misread as an off-toggle.
      applyPreset(preset, next);
    }
  };

  // Sheet-side removal (tab ×) reported back up — keep the list's toggle in
  // sync, and turn the preset off entirely if that was its last channel.
  // Uses a functional update (reading prior state, not the render-time
  // closure) because several tabs can close in quick succession - reading
  // `excludedByPreset` from the closure would lose all but the last one.
  const handlePresetPlatformRemoved = (channelId: string) => {
    if (!presetId) return;
    const preset = convertedSocialPresets.find((p: any) => p.id === presetId);
    const allPlatforms: string[] = (preset?.social || []).map(
      (item: any) => item.platform || ""
    );
    let becameFullyExcluded = false;
    setExcludedByPreset((prev) => {
      const excluded = prev[presetId] || [];
      if (excluded.includes(channelId)) return prev;
      const next = [...excluded, channelId];
      if (
        preset &&
        allPlatforms.length > 0 &&
        allPlatforms.every((p) => next.includes(p))
      ) {
        becameFullyExcluded = true;
        return prev; // removePresetEntirely below clears this preset's entry
      }
      return { ...prev, [presetId]: next };
    });
    if (becameFullyExcluded && preset) {
      removePresetEntirely(preset);
    }
  };

  // The sheet was dismissed without saving (outside click, Escape, or the
  // "Close without saving?" confirm) - always turn the whole preset off,
  // regardless of how many of its channels were actually open in the sheet,
  // instead of just excluding the ones that were open (handlePresetPlatformRemoved's
  // job, still used by the tab × so removing one destination there doesn't
  // kill sibling destinations still open in the same sheet).
  const handleSheetDismissed = () => {
    if (!presetId) return;
    const preset = convertedSocialPresets.find((p: any) => p.id === presetId);
    if (preset) removePresetEntirely(preset);
  };

  // Click on the preset name: if it's off, turn the whole preset on (the
  // sheet then opens on its own via the auto-open effect); if already on,
  // just (re)open/focus the sheet - the way back in after an outside-click
  // closed it.
  // Click on a single channel row's name: only opens the sheet if that
  // channel is already toggled on - it must never itself flip anything on,
  // that's the toggle switch's job alone.
  const handleOpenPresetChannel = (
    preset: PresetListModel,
    channelId?: string,
    isOn?: boolean
  ) => {
    if (!channelId) {
      if (!isOn) {
        applyPreset(preset, excludedByPreset[preset.id] || []);
        return;
      }
      const firstChannelId = (preset.social || []).find(
        (item: any) =>
          !(excludedByPreset[preset.id] || []).includes(item.platform || "")
      )?.platform;
      if (firstChannelId) {
        presetPlatformRef.current?.openChannel(firstChannelId);
      }
      return;
    }

    // Trust the toggle state PresetCardList already rendered (isOn) instead
    // of recomputing it from presetId/excludedByPreset here - a second,
    // independently-derived check can drift out of sync with what's on
    // screen and silently no-op a click that looks like it should work.
    if (isOn) {
      presetPlatformRef.current?.openChannel(channelId);
    }
  };

  return (
    <div className="mb-20 h-100 z-1">
      {currentMediaDetailsForEdit?.media?.media_status ===
        MEDIA_STATUS.MEDIA_STATUS_DRAFT &&
        isWizard &&
        currentMediaDetailsForEdit?.media?.content_type &&
        currentMediaDetailsForEdit?.media?.content_type !== 2 && (
          <Alert
            variant="warning"
            className="py-2 d-flex align-items-center position-fixed bottom-0 z-2"
            style={{ right: "100px" }}
          >
            Your content is in Draft. Set it to Active to publish.
            <i
              className="bi bi-caret-right-fill position-absolute f-20"
              style={{ right: "-15px", color: "#ffe59c" }}
            ></i>
          </Alert>
        )}

      <div
        className={`row m-0 ${isWizard ? "position-relative" : ""}`}
        style={{
          maxHeight: "100%",
          paddingBottom: "20px",
          height: "calc(100% - 50px)",
        }}
      >
        <div className={`col-sm-7 col-md-8 col-lg-9 p-0 pb-5`}>
          <div>
            <AllSocialPublish
              isWizard={isWizard}
              presetDetails={
                mediaData.social ? mediaData.social : mediaData || []
              }
              item={mediaData}
              presetList={presetList}
              metaPresetData={metaPresetData}
              onDirty={handlePublishDirty}
              presetPlatformRef={presetPlatformRef}
              onPresetPlatformRemoved={handlePresetPlatformRemoved}
              onSheetDismissed={handleSheetDismissed}
            />
          </div>
          {isOpenPreviewPreset && (
            <Modal
              title="Social Preview"
              isOpen={isOpenPreviewPreset}
              onClose={() => {
                setIsOpenPreviewPreset(false);
                setSocialPresetDetails({} as PresetListModel);
                setMediaData([]);
                setPresetId("");
              }}
              width="800px"
              footer={
                <Button
                  type="primary"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNext();
                  }}
                >
                  Next
                </Button>
              }
            >
              <AllSocialPublish
                isPreview={true}
                isWizard={false}
                isPreset={true}
                presetDetails={socialPresetDetails?.social || []}
                item={mediaData}
                handleAllSocialPresets={handleSocialPresetDetails}
              />
            </Modal>
          )}
          {isOpenSelectPreset && (
            <Modal
              isOpen={isOpenSelectPreset}
              onClose={() => {
                setIsOpenSelectPreset(false);
              }}
              title="Select Preset"
              width="990px"
              height="100%"
              closeOnOutsideClick={true}
            >
              <SelectPreset
                type="social"
                handleSelectPreset={handleSelectPreset}
                isWizard={false}
              />
            </Modal>
          )}
        </div>
        <div className={`side_form col-sm-5 col-md-4 col-lg-3`}>
          <div className="mt-3">
            <PresetCardList
              presets={convertedSocialPresets}
              selectedPresetId={presetId}
              onApply={handlePresetCardApply}
              excludedByPreset={excludedByPreset}
              onTogglePlatform={handleTogglePresetPlatform}
              onOpen={handleOpenPresetChannel}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  currentMediaDetailsForEdit: state.media.mediaDetails,
  presetList: state.media.presetList.list,
  singleCurrentUser: state.users.singleCurrentUser,
  bunchProductList: state.constants.bunchProductList.list,
  mediaAssets: state.media.mediaAssets.assets,
  adminPresetList: state.media.adminPresetList.list,
  borrowedMediaSocials: state.media.borrowedMediaSocials,
});

const mapDispatch = {
  getListMedia,
  updateModal,
  emptyAssetDetails,
  emptyMediaDetails,
  getAssets,
  getPresetList,
  getAdminPresetList,
  clearBorrowedMediaSocials,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector> & IWizardProps;

export default connector(MediaPublish);
